import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useSlips, useStorageSummary } from '@/features/storage/hooks'
import { SlipFormDialog } from '@/features/storage/slip-form-dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Warehouse, Anchor, Droplets, Sun, Building2, Plus, Ship, Search } from 'lucide-react'
import type { SlipStatus, SlipType } from '@/types/storage'
import {
  SLIP_STATUS_LABELS,
  SLIP_TYPE_LABELS,
  STORAGE_BUCKET_LABELS,
} from '@/types/storage'
import { requireFlow } from '@/features/auth/guards'

export const Route = createFileRoute('/_authenticated/storage/')({
  beforeLoad: requireFlow('storage'),
  // Type filter and search live in the URL so a refresh or shared link
  // restores the exact view.
  validateSearch: (search: Record<string, unknown>): { type?: string; q?: string } => ({
    type: typeof search.type === 'string' ? search.type : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: StorageGridPage,
})

const statusVariantMap: Record<SlipStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  available: 'default',
  occupied: 'secondary',
  reserved: 'outline',
  maintenance: 'destructive',
}

type FilterValue = 'all' | SlipType

function isFilterValue(value: unknown): value is FilterValue {
  return value === 'all' || value === 'wet' || value === 'dry' || value === 'warehouse'
}

const typeIcon: Record<SlipType, React.ComponentType<{ className?: string }>> = {
  wet: Droplets,
  dry: Sun,
  warehouse: Building2,
}

function StorageGridPage() {
  const { type, q } = Route.useSearch()
  const navigate = useNavigate({ from: '/storage/' })
  const filter: FilterValue = isFilterValue(type) ? type : 'all'
  const [searchInput, setSearchInput] = useState(q ?? '')

  const { data: slips, isLoading, isError } = useSlips(filter === 'all' ? undefined : filter)
  const { data: summary } = useStorageSummary()
  const [createOpen, setCreateOpen] = useState(false)

  const setFilter = (next: string) => {
    if (!isFilterValue(next)) return
    navigate({ search: (prev) => ({ ...prev, type: next === 'all' ? undefined : next }) })
  }

  const setSearch = (value: string) => {
    setSearchInput(value)
    navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true })
  }

  // Search is client-side: the yard is small enough to filter locally and it
  // matches across slip number and occupied boat name at once.
  const needle = (q ?? '').trim().toLowerCase()
  const visibleSlips = needle
    ? (slips ?? []).filter(
        (s) =>
          s.slip_number.toLowerCase().includes(needle) ||
          (s.current_boat_name ?? '').toLowerCase().includes(needle),
      )
    : (slips ?? [])

  if (isError) {
    return <div className="text-destructive">Failed to load storage data.</div>
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Warehouse className="h-8 w-8" /> Storage & Slips
          </h1>
          <p className="text-muted-foreground">
            Monitor boat allocations across wet slips and dry storage.
          </p>
        </div>
        <Button className="shrink-0" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Slip
        </Button>
      </div>

      {/* Summary cards — one per storage type */}
      <div className="grid gap-4 md:grid-cols-3">
        {(summary?.buckets ?? []).map((bucket) => {
          const Icon = typeIcon[bucket.type] ?? Anchor
          const occupancy =
            bucket.total === 0 ? 0 : Math.round((bucket.occupied / bucket.total) * 100)
          return (
            <Card key={bucket.type}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {STORAGE_BUCKET_LABELS[bucket.type]}
                </CardTitle>
                <Badge variant="outline">{occupancy}% full</Badge>
              </CardHeader>
              <CardContent className="text-2xl font-bold">
                {bucket.occupied} / {bucket.total}
                <p className="mt-1 text-xs font-normal text-muted-foreground">
                  {bucket.available} available · {bucket.reserved} reserved
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="wet">Wet Slips</TabsTrigger>
            <TabsTrigger value="dry">Dry Storage</TabsTrigger>
            <TabsTrigger value="warehouse">Warehouse</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search slip or boat…"
            value={searchInput}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {visibleSlips.map((slip) => (
            <Link to="/storage/$slipId" params={{ slipId: slip.id }} key={slip.id}>
              <Card className="hover:bg-accent transition-colors h-full">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Anchor className="h-5 w-5 text-muted-foreground" />
                    {slip.slip_number}
                  </CardTitle>
                  <Badge variant={statusVariantMap[slip.status]}>
                    {SLIP_STATUS_LABELS[slip.status]}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground">
                    Type: <span className="text-foreground">{SLIP_TYPE_LABELS[slip.type]}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Max Length: <span className="text-foreground">{slip.max_length_ft} ft</span>
                  </div>
                  {slip.current_boat_name && (
                    <div className="mt-2 text-sm font-medium truncate flex items-center gap-1.5">
                      <Ship className="h-4 w-4 shrink-0 text-muted-foreground" />
                      {slip.current_boat_name}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
          {visibleSlips.length === 0 && (
            <div className="col-span-full text-center text-muted-foreground py-12">
              {needle ? 'No slips match your search.' : 'No slips in this category.'}
            </div>
          )}
        </div>
      )}

      <SlipFormDialog open={createOpen} onOpenChange={setCreateOpen} mode="create" />
    </div>
  )
}
